import React, { useState, useMemo } from 'react';
import { TypographyStyledHeader, GridColStyled, BoxStyled } from './style';
import { Typography } from '@mui/material';
import CheckboxesGroup from '../nextSteps/checkBox';
import { NavigationButtons } from '../../shared/components/navigationButtons';
import { useAppSelector } from '../../store/hooks';
import { metaDataSelector } from '../../store/globalSelectors';
import { InfoModal } from '../../shared/components/modal/InfoModal';

export const NextSteps = () => {
  const { optedOptions } = useAppSelector(metaDataSelector);
  const { routeObj } = optedOptions;
  const [openInfoModal, setOpenInfoModal] = useState(false);

  // Compute the first route based on the selected options.
  // If multiple options are chosen, the one with the highest priority is used.
  // Option 1 (ES) takes precedence, then Option 2 (MAL), then Option 3 (MUS).
  // If no option is selected, we return an empty string.
  const computedNextRoute = useMemo(() => {
    const option1 = routeObj?.estimating_requirements; // Option 1 (ES)
    const option2 = routeObj?.malnutrition_screening; // Option 2 (MAL)
    const option3 = routeObj?.muscle_screening; // Option 3 (MUS)

    // If no options are selected, return an empty string.
    if (!option1 && !option2 && !option3) {
      return '';
    }

    // Return the highest-priority route.
    if (option1) return '/estimating-requirements';
    if (option2) return '/malnutrition-screening';
    if (option3) return '/muscle-screening';

    // Fallback (should not occur)
    return '';
  }, [routeObj]);

  // Handle the click on the Next button.
  // If no valid next route exists (i.e. no option is selected), prevent navigation and show the modal.
  const handleNextClick = (e) => {
    if (!computedNextRoute) {
      // Prevent NavigationButtons from navigating.
      e.preventDefault();
      setOpenInfoModal(true);
    }
    // Otherwise, allow NavigationButtons to do its navigation logic.
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleNextClick(e);
    }
  };

  return (
    <>
      <BoxStyled>
        <TypographyStyledHeader
          variant="h1"
          style={{
            fontWeight: 'bolder',
            lineHeight: '1.875rem'
          }}
        >
          NEXT STEPS
        </TypographyStyledHeader>
        <GridColStyled
          container
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '1.875rem',
            background: '#fff',
            borderRadius: '0.9375rem',
            marginTop: '1.875rem'
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontFamily: 'Calibri',
              fontSize: '1rem',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '1.25rem',
              textAlign: 'left'
            }}
          >
            Choose from the following options:
          </Typography>
          <CheckboxesGroup />
        </GridColStyled>
      </BoxStyled>

      {/* Wrap NavigationButtons in a container that intercepts the click */}
      <div
        onClick={handleNextClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        style={{ marginTop: 'auto' }}
      >
        <NavigationButtons nextDisabled={false} nextRoute={computedNextRoute} />
      </div>

      <InfoModal
        open={openInfoModal}
        onClose={() => setOpenInfoModal(false)}
        title=""
        content={
          <>
            You must select <strong>at least one option</strong> to continue.
          </>
        }
        customJustifyContent="center"
      />
    </>
  );
};
